import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import type {
  AutopilotMode,
  AutopilotSettings,
  DoctorAutopilot,
  FaixaDeHorario,
  MemoryContent,
  SimulatorResult,
  SimulatorTurn,
  Uuid,
} from '@zenda/api-contract';
import { EmptyState } from './EmptyState';
import { Icon, type IconName } from './Icon';
import { SkeletonRows } from './Skeleton';
import { useTip } from './Tip';
import { useT } from '../lib/i18n';
import { formatDateShort } from '../lib/format';
import { textoDoErro } from '../lib/aviso-de-erro';
import { useAbaDoPiloto, useAcoesDaAba, useAprendizado } from '../lib/piloto';
import '../styles/piloto.css';

const novoId = (): string => Math.random().toString(36).slice(2, 10);

type Secao = 'resumo' | 'memoria' | 'estilo' | 'horarios' | 'testar' | 'aprendizados' | 'historico';

const SECOES: readonly { readonly id: Secao; readonly icone: IconName }[] = [
  { id: 'resumo', icone: 'grid' },
  { id: 'memoria', icone: 'note' },
  { id: 'estilo', icone: 'chat' },
  { id: 'horarios', icone: 'clock' },
  { id: 'testar', icone: 'play' },
  { id: 'aprendizados', icone: 'robot' },
  { id: 'historico', icone: 'checks' },
];

/** Uma lista editável de faixas semanais, "seg 08:00 às 18:00" (plano §8.2). */
export function EditorDeFaixas({
  faixas,
  onMudar,
}: {
  readonly faixas: readonly FaixaDeHorario[];
  readonly onMudar: (f: FaixaDeHorario[]) => void;
}): ReactElement {
  const t = useT();
  const trocar = (i: number, campo: keyof FaixaDeHorario, v: string) =>
    onMudar(
      faixas.map((f, j) => (j === i ? { ...f, [campo]: campo === 'dia' ? Number(v) : v } : f)),
    );
  return (
    <div className="piloto-faixas">
      {faixas.map((f, i) => (
        <div className="piloto-faixas__linha" key={i}>
          <select
            className="piloto-campo"
            value={f.dia}
            onChange={(e) => trocar(i, 'dia', e.target.value)}
          >
            {t.piloto.dias.map((d, n) => (
              <option key={d} value={n}>
                {d}
              </option>
            ))}
          </select>
          <input
            className="piloto-campo"
            type="time"
            value={f.inicio}
            onChange={(e) => trocar(i, 'inicio', e.target.value)}
          />
          <input
            className="piloto-campo"
            type="time"
            value={f.fim}
            onChange={(e) => trocar(i, 'fim', e.target.value)}
          />
          <BotaoRemover onClick={() => onMudar(faixas.filter((_, j) => j !== i))} />
        </div>
      ))}
      <div>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() =>
            onMudar(
              faixas.length === 0
                ? [1, 2, 3, 4, 5].map((dia) => ({ dia, inicio: '08:00', fim: '18:00' }))
                : [...faixas, { dia: 6, inicio: '08:00', fim: '12:00' }],
            )
          }
        >
          <Icon name="plus" size={13} />
        </button>
      </div>
    </div>
  );
}

function BotaoRemover({ onClick }: { readonly onClick: () => void }): ReactElement {
  return (
    <button type="button" className="piloto-lista__remover" onClick={onClick} aria-label="remover">
      <Icon name="x" size={13} />
    </button>
  );
}

function BotaoAdicionar({
  rotulo,
  onClick,
}: {
  readonly rotulo: string;
  readonly onClick: () => void;
}): ReactElement {
  return (
    <button type="button" className="piloto-lista__add" onClick={onClick}>
      <Icon name="plus" size={13} />
      {rotulo}
    </button>
  );
}

/** Um bloco com título e uma linha que diz para que serve. */
function Bloco({
  titulo,
  texto,
  acao,
  children,
}: {
  readonly titulo: string;
  readonly texto?: string;
  readonly acao?: ReactNode;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <section className="piloto-bloco">
      <div className="piloto-bloco__cabeca">
        <div>
          <h4 className="piloto-bloco__titulo">{titulo}</h4>
          {texto === undefined ? null : <p className="piloto-bloco__texto">{texto}</p>}
        </div>
        {acao}
      </div>
      {children}
    </section>
  );
}

// ── Resumo ──────────────────────────────────────────────────────────────────

const MODOS_DO_MEDICO: readonly (AutopilotMode | null)[] = [null, 'off', 'copiloto', 'piloto'];

function Resumo({
  aba,
  clientId,
  ir,
}: {
  readonly aba: DoctorAutopilot;
  readonly clientId: Uuid;
  readonly ir: (s: Secao) => void;
}): ReactElement {
  const t = useT();
  const a = t.piloto.aba;
  const { config } = useAcoesDaAba(clientId);
  const testados = aba.tests.filter((c) => c.lastResult !== null);
  const passaram = testados.filter((c) => c.lastResult?.ok === true).length;
  const memoriaOk = aba.memory.sobre.length + aba.memory.perguntas.length > 0;
  const testesOk = testados.length > 0 && passaram === testados.length;

  const itens: { ok: boolean; texto: string; secao: Secao | null }[] = [
    { ok: aba.providerReady, texto: aba.providerReady ? a.checkIa : a.iaFora, secao: null },
    {
      ok: memoriaOk,
      texto: a.checkMemoria(aba.memory.sobre.length, aba.memory.perguntas.length),
      secao: 'memoria',
    },
    {
      ok: testesOk,
      texto:
        aba.tests.length === 0 ? a.checkTestesNenhum : a.checkTestes(passaram, aba.tests.length),
      secao: 'testar',
    },
    {
      ok: aba.learnings.length === 0,
      texto: a.checkAprendizados(aba.learnings.length),
      secao: 'aprendizados',
    },
  ];

  return (
    <>
      <Bloco titulo={a.posicao} texto={a.posicaoTexto}>
        <div className="piloto-modos piloto-modos--grande" role="radiogroup" aria-label={a.posicao}>
          {MODOS_DO_MEDICO.map((m) => (
            <button
              key={m ?? 'herdar'}
              type="button"
              role="radio"
              aria-checked={aba.own.mode === m}
              className={`piloto-modos__item${aba.own.mode === m ? ' is-on' : ''}${m === null ? '' : ` piloto-modos__item--${m}`}`}
              disabled={config.isPending}
              onClick={() => config.mutate({ mode: m })}
            >
              {m === null ? t.piloto.herdar : t.piloto.modos[m]}
            </button>
          ))}
        </div>
        <p className="piloto-bloco__texto">
          <b>{a.vale(t.piloto.modos[aba.effectiveMode])}.</b>{' '}
          {t.piloto.modosLongos[aba.effectiveMode]}
        </p>
      </Bloco>

      <Bloco titulo={a.pronto}>
        <ul className="piloto-check-lista">
          {itens.map((i) => (
            <li key={i.texto} className={i.ok ? 'is-ok' : ''}>
              <span className="piloto-check-lista__marca" aria-hidden="true">
                {i.ok ? <Icon name="check" size={12} /> : null}
              </span>
              <span>{i.texto}</span>
              {i.secao === null || i.ok ? null : (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => ir(i.secao!)}
                >
                  {a.ir}
                </button>
              )}
            </li>
          ))}
        </ul>
      </Bloco>

      <Bloco titulo={a.mes}>
        <div className="piloto-numeros">
          <div className="piloto-numero">
            <b className="num">{aba.month.answered}</b>
            <span>{a.respondidasMes}</span>
          </div>
          <div className="piloto-numero">
            <b className="num">{aba.month.escalated}</b>
            <span>{a.escaladasMes}</span>
          </div>
          <div className="piloto-numero">
            <b className="num">
              {aba.month.costBrl.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
                maximumFractionDigits: 4,
              })}
            </b>
            <span>{a.custoMes}</span>
          </div>
        </div>
        <p className="piloto-bloco__texto">
          {a.sombra}: {a.sombraTexto(aba.shadow.agreed, aba.shadow.compared)}
        </p>
        {aba.month.topReasons.length === 0 ? null : (
          <>
            <p className="piloto-sub">{a.motivosMes}</p>
            <ul className="piloto-lista-simples">
              {aba.month.topReasons.map((r) => (
                <li key={r.reason}>
                  <span>{r.reason}</span>
                  <span className="num">{r.count}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </Bloco>
    </>
  );
}

// ── Memória e estilo: um rascunho só, salvo de uma vez ──────────────────────

interface Rascunho {
  readonly m: MemoryContent;
  readonly mudar: (parcial: Partial<MemoryContent>) => void;
}

/** O seletor "paciente pode saber / só a IA sabe" de cada informação (plano §8.2). */
function Visibilidade({
  pode,
  onMudar,
}: {
  readonly pode: boolean;
  readonly onMudar: (v: boolean) => void;
}): ReactElement {
  const t = useT();
  const tip = useTip();
  const rotulo = pode ? t.piloto.aba.pacienteVe : t.piloto.aba.pacienteNaoVe;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={pode}
      aria-label={rotulo}
      className={`piloto-olho${pode ? '' : ' is-oculto'}`}
      onClick={() => onMudar(!pode)}
      {...tip(rotulo)}
    >
      <Icon name={pode ? 'eye' : 'eye-off'} size={15} />
    </button>
  );
}

function Memoria({
  aba,
  clientId,
  r,
}: {
  readonly aba: DoctorAutopilot;
  readonly clientId: Uuid;
  readonly r: Rascunho;
}): ReactElement {
  const t = useT();
  const a = t.piloto.aba;
  const { importar } = useAcoesDaAba(clientId);
  const { m, mudar } = r;
  const cheia = aba.memoryTokens > aba.memoryLimit;

  return (
    <>
      <div className="piloto-medidor-linha">
        <div className={`piloto-medidor${cheia ? ' is-cheio' : ''}`}>
          <span
            style={{ width: `${Math.min(100, (aba.memoryTokens / aba.memoryLimit) * 100)}%` }}
          />
        </div>
        <span className="num">{a.memoriaTamanho(aba.memoryTokens, aba.memoryLimit)}</span>
      </div>
      {cheia ? <p className="piloto-bloco__texto">{a.memoriaCheia}</p> : null}

      <Bloco titulo={a.informacoes} texto={a.informacoesTexto}>
        <div className="piloto-lista">
          {m.sobre.length === 0 ? null : (
            <div className="piloto-lista__cabeca piloto-lista__linha--info">
              <span>{a.colunaAssunto}</span>
              <span>{a.colunaResposta}</span>
              <span />
              <span />
            </div>
          )}
          {m.sobre.map((c, i) => (
            <div className="piloto-lista__linha piloto-lista__linha--info" key={c.id}>
              <input
                className="piloto-campo piloto-campo--leve"
                placeholder={a.rotulo}
                value={c.rotulo}
                maxLength={80}
                onChange={(e) =>
                  mudar({
                    sobre: m.sobre.map((x, j) => (j === i ? { ...x, rotulo: e.target.value } : x)),
                  })
                }
              />
              <input
                className="piloto-campo piloto-campo--leve"
                placeholder={a.textoDoCampo}
                value={c.texto}
                maxLength={600}
                onChange={(e) =>
                  mudar({
                    sobre: m.sobre.map((x, j) => (j === i ? { ...x, texto: e.target.value } : x)),
                  })
                }
              />
              <Visibilidade
                pode={c.podeInformar}
                onMudar={(v) =>
                  mudar({ sobre: m.sobre.map((x, j) => (j === i ? { ...x, podeInformar: v } : x)) })
                }
              />
              <BotaoRemover onClick={() => mudar({ sobre: m.sobre.filter((_, j) => j !== i) })} />
            </div>
          ))}
          <BotaoAdicionar
            rotulo={a.novoCampo}
            onClick={() =>
              mudar({
                sobre: [...m.sobre, { id: novoId(), rotulo: '', texto: '', podeInformar: true }],
              })
            }
          />
        </div>
      </Bloco>

      <Bloco
        titulo={a.perguntas}
        texto={a.perguntasTexto}
        acao={
          aba.quickRepliesAvailable === 0 ? undefined : (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => importar.mutate()}
              disabled={importar.isPending}
            >
              {a.importar(aba.quickRepliesAvailable)}
            </button>
          )
        }
      >
        <div className="piloto-lista">
          {m.perguntas.map((p, i) => (
            <div className="piloto-lista__linha piloto-lista__linha--qa" key={p.id}>
              <div className="piloto-qa">
                <input
                  className="piloto-campo piloto-campo--leve piloto-qa__p"
                  placeholder={a.pergunta}
                  value={p.pergunta}
                  maxLength={300}
                  onChange={(e) =>
                    mudar({
                      perguntas: m.perguntas.map((x, j) =>
                        j === i ? { ...x, pergunta: e.target.value } : x,
                      ),
                    })
                  }
                />
                <textarea
                  className="piloto-campo piloto-campo--leve"
                  rows={1}
                  placeholder={a.colunaResposta}
                  value={p.resposta}
                  maxLength={600}
                  onChange={(e) =>
                    mudar({
                      perguntas: m.perguntas.map((x, j) =>
                        j === i ? { ...x, resposta: e.target.value } : x,
                      ),
                    })
                  }
                />
                {p.aprendidoEm === null ? null : (
                  <span className="piloto-qa__aprendido">
                    <Icon name="robot" size={11} /> {a.aprendidoEm(p.aprendidoEm)}
                  </span>
                )}
              </div>
              <BotaoRemover
                onClick={() => mudar({ perguntas: m.perguntas.filter((_, j) => j !== i) })}
              />
            </div>
          ))}
          <BotaoAdicionar
            rotulo={a.novaPergunta}
            onClick={() =>
              mudar({
                perguntas: [
                  ...m.perguntas,
                  { id: novoId(), pergunta: '', resposta: '', aprendidoEm: null },
                ],
              })
            }
          />
        </div>
      </Bloco>

      <Bloco titulo={a.regras} texto={a.regrasTexto2}>
        <div className="piloto-lista">
          {m.regras.map((regra, i) => (
            <div className="piloto-lista__linha piloto-lista__linha--regra" key={i}>
              <input
                className="piloto-campo piloto-campo--leve"
                value={regra}
                maxLength={300}
                onChange={(e) =>
                  mudar({ regras: m.regras.map((x, j) => (j === i ? e.target.value : x)) })
                }
              />
              <BotaoRemover onClick={() => mudar({ regras: m.regras.filter((_, j) => j !== i) })} />
            </div>
          ))}
          <BotaoAdicionar
            rotulo={a.novaRegra}
            onClick={() => mudar({ regras: [...m.regras, ''] })}
          />
        </div>
      </Bloco>
    </>
  );
}

function Estilo({
  aba,
  clientId,
  r,
}: {
  readonly aba: DoctorAutopilot;
  readonly clientId: Uuid;
  readonly r: Rascunho;
}): ReactElement {
  const t = useT();
  const a = t.piloto.aba;
  const { estilo: sugerir } = useAcoesDaAba(clientId);
  const { m, mudar } = r;
  const estilo = (parcial: Partial<MemoryContent['estilo']>) =>
    mudar({ estilo: { ...m.estilo, ...parcial } });

  return (
    <>
      <Bloco
        titulo={a.estilo}
        texto={a.estiloTexto}
        acao={
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => sugerir.mutate(undefined, { onSuccess: (e) => mudar({ estilo: e }) })}
            disabled={sugerir.isPending || !aba.providerReady}
          >
            <Icon name="robot" size={13} />
            {a.rascunhoDeEstilo}
          </button>
        }
      >
        {sugerir.isError ? (
          <p className="piloto-bloco__texto">{textoDoErro(sugerir.error)}</p>
        ) : null}
        <div className="piloto-grade">
          <label>
            {a.tratamento}
            <select
              className="piloto-campo"
              value={m.estilo.tratamento}
              onChange={(e) => estilo({ tratamento: e.target.value as 'voce' | 'senhor' })}
            >
              <option value="voce">{a.voce}</option>
              <option value="senhor">{a.senhor}</option>
            </select>
          </label>
          <label>
            {a.formalidade}
            <select
              className="piloto-campo"
              value={m.estilo.formalidade}
              onChange={(e) =>
                estilo({ formalidade: e.target.value as MemoryContent['estilo']['formalidade'] })
              }
            >
              {(['informal', 'neutra', 'formal'] as const).map((f) => (
                <option key={f} value={f}>
                  {a.formalidades[f]}
                </option>
              ))}
            </select>
          </label>
          <label>
            {a.assinatura}
            <input
              className="piloto-campo"
              value={m.estilo.assinatura}
              maxLength={80}
              onChange={(e) => estilo({ assinatura: e.target.value })}
            />
          </label>
          <label className="piloto-check">
            <input
              type="checkbox"
              checked={m.estilo.emoji}
              onChange={(e) => estilo({ emoji: e.target.checked })}
            />
            {a.emoji}
          </label>
        </div>
        <label className="piloto-rotulo">
          {a.frases}
          <textarea
            className="piloto-campo"
            rows={2}
            value={m.estilo.frases.join('\n')}
            onChange={(e) => estilo({ frases: e.target.value.split('\n') })}
          />
        </label>
        <label className="piloto-rotulo">
          {a.observacoes}
          <textarea
            className="piloto-campo"
            rows={2}
            value={m.estilo.observacoes}
            maxLength={400}
            onChange={(e) => estilo({ observacoes: e.target.value })}
          />
        </label>
      </Bloco>

      <Bloco titulo={a.exemplos} texto={a.exemplosTexto}>
        <div className="piloto-lista">
          {m.exemplos.map((x, i) => (
            <div className="piloto-lista__linha piloto-lista__linha--qa" key={i}>
              <div className="piloto-qa">
                <input
                  className="piloto-campo piloto-campo--leve piloto-qa__p"
                  placeholder={a.paciente}
                  value={x.paciente}
                  maxLength={300}
                  onChange={(e) =>
                    mudar({
                      exemplos: m.exemplos.map((y, j) =>
                        j === i ? { ...y, paciente: e.target.value } : y,
                      ),
                    })
                  }
                />
                <textarea
                  className="piloto-campo piloto-campo--leve"
                  rows={1}
                  placeholder={a.eu}
                  value={x.resposta}
                  maxLength={400}
                  onChange={(e) =>
                    mudar({
                      exemplos: m.exemplos.map((y, j) =>
                        j === i ? { ...y, resposta: e.target.value } : y,
                      ),
                    })
                  }
                />
              </div>
              <BotaoRemover
                onClick={() => mudar({ exemplos: m.exemplos.filter((_, j) => j !== i) })}
              />
            </div>
          ))}
          {m.exemplos.length >= 10 ? null : (
            <BotaoAdicionar
              rotulo={a.novoExemplo}
              onClick={() => mudar({ exemplos: [...m.exemplos, { paciente: '', resposta: '' }] })}
            />
          )}
        </div>
      </Bloco>
    </>
  );
}

// ── Quando responde ─────────────────────────────────────────────────────────

function Horarios({
  aba,
  clientId,
}: {
  readonly aba: DoctorAutopilot;
  readonly clientId: Uuid;
}): ReactElement {
  const t = useT();
  const a = t.piloto.aba;
  const { config } = useAcoesDaAba(clientId);
  const inicial = (): Partial<AutopilotSettings> => ({
    replyHours: aba.own.replyHours,
    threshold: aba.own.threshold,
    minNoticeMinutes: aba.own.minNoticeMinutes,
    maxConsecutive: aba.own.maxConsecutive,
    humanActiveMinutes: aba.own.humanActiveMinutes,
    rhythm: aba.own.rhythm,
    emergencyMessage: aba.own.emergencyMessage,
    reserveInHours: aba.own.reserveInHours,
    reserveOutHours: aba.own.reserveOutHours,
  });
  const [c, setC] = useState<Partial<AutopilotSettings>>(inicial);
  const [sujo, setSujo] = useState(false);
  // Pelo que está salvo, e não pelo objeto: o stream busca a aba de novo a cada mensagem, e
  // reiniciar pelo objeto apagaria o que ela está digitando.
  const salvo = JSON.stringify(aba.own);
  useEffect(() => {
    setC(inicial());
    setSujo(false);
  }, [salvo]);
  const mudar = (p: Partial<AutopilotSettings>) => {
    setSujo(true);
    setC((x) => ({ ...x, ...p }));
  };
  const num = (v: string): number | null => (v.trim() === '' ? null : Number(v));

  return (
    <>
      <Bloco titulo={a.respondeQuando}>
        <div
          className="piloto-modos piloto-modos--grande"
          role="radiogroup"
          aria-label={a.respondeQuando}
        >
          {(['sempre', 'faixas'] as const).map((o) => {
            const on = (o === 'sempre') === (c.replyHours === null || c.replyHours === undefined);
            return (
              <button
                key={o}
                type="button"
                role="radio"
                aria-checked={on}
                className={`piloto-modos__item${on ? ' is-on' : ''}`}
                onClick={() =>
                  mudar({
                    replyHours:
                      o === 'sempre'
                        ? null
                        : [1, 2, 3, 4, 5].map((dia) => ({ dia, inicio: '08:00', fim: '20:00' })),
                  })
                }
              >
                {o === 'sempre' ? a.sempre : a.soNestes}
              </button>
            );
          })}
        </div>
        {c.replyHours === null || c.replyHours === undefined ? null : (
          <EditorDeFaixas faixas={c.replyHours} onMudar={(f) => mudar({ replyHours: f })} />
        )}
      </Bloco>

      <Bloco titulo={a.ritmoTitulo} texto={a.ritmoTexto}>
        <div className="piloto-grade">
          <label>
            {a.esperaMin}
            <input
              className="piloto-campo"
              type="number"
              placeholder="45"
              value={c.rhythm?.esperaMinS ?? ''}
              onChange={(e) =>
                mudar({
                  rhythm:
                    e.target.value === ''
                      ? null
                      : {
                          esperaMinS: Number(e.target.value),
                          esperaMaxS: c.rhythm?.esperaMaxS ?? 180,
                        },
                })
              }
            />
          </label>
          <label>
            {a.esperaMax}
            <input
              className="piloto-campo"
              type="number"
              placeholder="180"
              value={c.rhythm?.esperaMaxS ?? ''}
              onChange={(e) =>
                mudar({
                  rhythm:
                    e.target.value === ''
                      ? null
                      : {
                          esperaMinS: c.rhythm?.esperaMinS ?? 45,
                          esperaMaxS: Number(e.target.value),
                        },
                })
              }
            />
          </label>
        </div>
      </Bloco>

      <Bloco titulo={a.limitesTitulo} texto={a.limitesTexto}>
        <div className="piloto-grade">
          <label>
            {a.limiar}
            <input
              className="piloto-campo"
              type="number"
              step="0.05"
              min="0"
              max="1"
              placeholder="0.8"
              value={c.threshold ?? ''}
              onChange={(e) => mudar({ threshold: num(e.target.value) })}
            />
          </label>
          <label>
            {a.seguidas}
            <input
              className="piloto-campo"
              type="number"
              placeholder="6"
              value={c.maxConsecutive ?? ''}
              onChange={(e) => mudar({ maxConsecutive: num(e.target.value) })}
            />
          </label>
          <label>
            {a.humanoAtivo}
            <input
              className="piloto-campo"
              type="number"
              placeholder="10"
              value={c.humanActiveMinutes ?? ''}
              onChange={(e) => mudar({ humanActiveMinutes: num(e.target.value) })}
            />
          </label>
          <label>
            {a.antecedencia}
            <input
              className="piloto-campo"
              type="number"
              placeholder="120"
              value={c.minNoticeMinutes ?? ''}
              onChange={(e) => mudar({ minNoticeMinutes: num(e.target.value) })}
            />
          </label>
        </div>
      </Bloco>

      <Bloco titulo={a.frasesTitulo}>
        <label className="piloto-rotulo">
          {a.emergencia}
          <textarea
            className="piloto-campo"
            rows={2}
            placeholder={aba.operation.emergencyMessage ?? ''}
            value={c.emergencyMessage ?? ''}
            maxLength={400}
            onChange={(e) => mudar({ emergencyMessage: e.target.value })}
          />
          <span>{a.emergenciaTexto}</span>
        </label>
        <label className="piloto-rotulo">
          {a.reservaDentro}
          <input
            className="piloto-campo"
            value={c.reserveInHours ?? ''}
            maxLength={300}
            placeholder="Vou reservar esse horário e já te confirmo."
            onChange={(e) => mudar({ reserveInHours: e.target.value })}
          />
        </label>
        <label className="piloto-rotulo">
          {a.reservaFora}
          <input
            className="piloto-campo"
            value={c.reserveOutHours ?? ''}
            maxLength={300}
            placeholder="Vou reservar esse horário e te confirmo assim que abrir a agenda."
            onChange={(e) => mudar({ reserveOutHours: e.target.value })}
          />
        </label>
      </Bloco>

      {sujo ? (
        <BarraDeSalvar
          salvando={config.isPending}
          erro={config.isError ? textoDoErro(config.error) : null}
          onDescartar={() => {
            setC(inicial());
            setSujo(false);
          }}
          onSalvar={() => config.mutate(c)}
        />
      ) : null}
    </>
  );
}

/** Aparece só quando há o que salvar: o rodapé fixo da seção. */
function BarraDeSalvar({
  salvando,
  erro,
  onSalvar,
  onDescartar,
}: {
  readonly salvando: boolean;
  readonly erro: string | null;
  readonly onSalvar: () => void;
  readonly onDescartar: () => void;
}): ReactElement {
  const t = useT();
  return (
    <div className="piloto-salvar" role="status">
      <span>{erro ?? t.piloto.aba.naoSalvo}</span>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={onDescartar}
        disabled={salvando}
      >
        {t.piloto.aba.descartarAlteracoes}
      </button>
      <button
        type="button"
        className="btn btn--primary btn--sm"
        onClick={onSalvar}
        disabled={salvando}
      >
        {t.piloto.aba.salvarConfig}
      </button>
    </div>
  );
}

// ── Testar ──────────────────────────────────────────────────────────────────

interface LinhaDoSim {
  readonly de: 'paciente' | 'piloto' | 'nota';
  readonly texto: string;
}

/** O simulador: ela conversa como paciente, e nada vai para o WhatsApp (plano §13). */
function Simulador({ clientId }: { readonly clientId: Uuid }): ReactElement {
  const t = useT();
  const a = t.piloto.aba;
  const { simular } = useAcoesDaAba(clientId);
  const [linhas, setLinhas] = useState<LinhaDoSim[]>([]);
  const [oferecidos, setOferecidos] = useState<readonly string[]>([]);
  const [texto, setTexto] = useState('');
  const fio = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (fio.current !== null) fio.current.scrollTop = fio.current.scrollHeight;
  }, [linhas.length]);

  function enviar(): void {
    const msg = texto.trim();
    if (msg === '' || simular.isPending) return;
    const proximas: LinhaDoSim[] = [...linhas, { de: 'paciente', texto: msg }];
    setLinhas(proximas);
    setTexto('');
    const turnos: SimulatorTurn[] = proximas
      .filter((l): l is LinhaDoSim & { de: 'paciente' | 'piloto' } => l.de !== 'nota')
      .map((l) => ({ de: l.de, texto: l.texto }));
    simular.mutate(
      { turnos, oferecidos },
      {
        onSuccess: (r: SimulatorResult) => {
          const nota = [
            a.acoes[r.action],
            r.reason === null ? null : a.porque(r.reason),
            r.sources.length === 0 ? null : `${a.fontes}: ${r.sources.join(', ')}`,
          ]
            .filter((x) => x !== null)
            .join(' · ');
          setLinhas((atual) => [
            ...atual,
            ...(r.text === null ? [] : [{ de: 'piloto' as const, texto: r.text }]),
            { de: 'nota', texto: nota },
          ]);
          if (r.offered.length > 0) setOferecidos(r.offered);
        },
        onError: (e) => setLinhas((atual) => [...atual, { de: 'nota', texto: textoDoErro(e) }]),
      },
    );
  }

  return (
    <Bloco
      titulo={a.simulador}
      acao={
        linhas.length === 0 ? undefined : (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              setLinhas([]);
              setOferecidos([]);
            }}
          >
            {a.limpar}
          </button>
        )
      }
    >
      <div className="piloto-sim">
        <div className="piloto-sim__fio" ref={fio} aria-live="polite">
          {linhas.length === 0 ? <p className="piloto-sim__nota">{a.simuladorTexto}</p> : null}
          {linhas.map((l, i) =>
            l.de === 'nota' ? (
              <p className="piloto-sim__nota" key={i}>
                {l.texto}
              </p>
            ) : (
              <p className={`piloto-sim__msg piloto-sim__msg--${l.de}`} key={i}>
                {l.texto}
              </p>
            ),
          )}
          {simular.isPending ? <p className="piloto-sim__nota">…</p> : null}
        </div>
        <div className="piloto-sim__entrada">
          <input
            className="piloto-campo"
            value={texto}
            maxLength={500}
            placeholder={a.escreva}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') enviar();
            }}
          />
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={enviar}
            disabled={simular.isPending}
          >
            {a.enviar}
          </button>
        </div>
      </div>
    </Bloco>
  );
}

function Testes({
  aba,
  clientId,
}: {
  readonly aba: DoctorAutopilot;
  readonly clientId: Uuid;
}): ReactElement {
  const t = useT();
  const a = t.piloto.aba;
  const acoes = useAcoesDaAba(clientId);
  const [pergunta, setPergunta] = useState('');
  const [esperado, setEsperado] = useState('');
  const [acao, setAcao] = useState<'responder' | 'escalar'>('responder');
  const adicionar = () => {
    if (pergunta.trim() === '') return;
    acoes.novoTeste.mutate(
      { question: pergunta, expected: esperado, expectAction: acao },
      {
        onSuccess: () => {
          setPergunta('');
          setEsperado('');
        },
      },
    );
  };
  return (
    <Bloco
      titulo={a.testes}
      texto={a.testesTexto}
      acao={
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => acoes.rodarTestes.mutate()}
          disabled={acoes.rodarTestes.isPending || aba.tests.length === 0}
        >
          <Icon name="play" size={13} />
          {a.rodar}
        </button>
      }
    >
      <div className="piloto-lista">
        {aba.tests.map((c) => (
          <div className="piloto-teste" key={c.id}>
            <span
              className={`piloto-teste__res ${c.lastResult === null ? '' : c.lastResult.ok ? 'is-ok' : 'is-falha'}`}
              aria-label={c.lastResult === null ? '' : c.lastResult.ok ? a.passou : a.falhou}
            >
              {c.lastResult === null ? (
                '·'
              ) : c.lastResult.ok ? (
                <Icon name="check" size={12} />
              ) : (
                <Icon name="x" size={12} />
              )}
            </span>
            <span className="piloto-teste__pergunta">
              {c.question}
              {c.lastResult === null ? null : (
                <span className="piloto-teste__det">
                  {a.acoes[c.lastResult.action as keyof typeof a.acoes] ?? c.lastResult.action}
                  {c.lastResult.text === null ? '' : `: "${c.lastResult.text}"`}
                </span>
              )}
            </span>
            <BotaoRemover onClick={() => acoes.apagarTeste.mutate(c.id)} />
          </div>
        ))}
        <div className="piloto-teste-novo">
          <input
            className="piloto-campo"
            placeholder={a.pergunta}
            value={pergunta}
            maxLength={300}
            onChange={(e) => setPergunta(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') adicionar();
            }}
          />
          <select
            className="piloto-campo"
            value={acao}
            aria-label={a.esperaAcao}
            onChange={(e) => setAcao(e.target.value as 'responder' | 'escalar')}
          >
            <option value="responder">{a.deveResponder}</option>
            <option value="escalar">{a.deveEscalar}</option>
          </select>
          {acao === 'responder' ? (
            <input
              className="piloto-campo"
              placeholder={a.esperado}
              value={esperado}
              maxLength={300}
              onChange={(e) => setEsperado(e.target.value)}
            />
          ) : (
            <span />
          )}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={pergunta.trim() === '' || acoes.novoTeste.isPending}
            onClick={adicionar}
          >
            <Icon name="plus" size={13} />
            {a.adicionarTeste}
          </button>
        </div>
      </div>
    </Bloco>
  );
}

// ── Aprendizados e histórico ────────────────────────────────────────────────

function Aprendizados({ aba }: { readonly aba: DoctorAutopilot }): ReactElement {
  const t = useT();
  const a = t.piloto.aba;
  const { aprovar, descartar } = useAprendizado();
  if (aba.learnings.length === 0) {
    return <p className="piloto-bloco__texto piloto-vazio">{a.semAprendizados}</p>;
  }
  return (
    <div className="piloto-lista">
      {aba.learnings.map((l) => (
        <div className="piloto-aprendizado" key={l.id}>
          <div className="piloto-aprendizado__qa">
            <b>{l.question}</b>
            <span>{l.answer}</span>
          </div>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => descartar.mutate(l.id)}
          >
            {t.piloto.descartar}
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => aprovar.mutate({ id: l.id, question: l.question, answer: l.answer })}
          >
            {a.aprovar}
          </button>
        </div>
      ))}
    </div>
  );
}

function Historico({
  aba,
  clientId,
}: {
  readonly aba: DoctorAutopilot;
  readonly clientId: Uuid;
}): ReactElement {
  const t = useT();
  const a = t.piloto.aba;
  const { restaurar } = useAcoesDaAba(clientId);
  if (aba.versions.length === 0)
    return <p className="piloto-bloco__texto piloto-vazio">{a.vazio}</p>;
  return (
    <div className="piloto-lista">
      {aba.versions.map((v) => (
        <div className="piloto-versao" key={v.version}>
          <span>{a.versao(v.version, formatDateShort(v.createdAt), v.createdBy)}</span>
          {v.version === aba.memoryVersion ? (
            <Icon name="check" size={13} />
          ) : (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => restaurar.mutate(v.version)}
              disabled={restaurar.isPending}
            >
              {a.restaurar}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// ── A aba ───────────────────────────────────────────────────────────────────

/**
 * A aba "Piloto automático" no detalhe do cliente (plano §16): uma seção por pergunta, em
 * vez de tudo empilhado. Memória e estilo são o mesmo documento versionado, então editam
 * um rascunho só e salvam juntos.
 */
export function PilotoTab({ clientId }: { readonly clientId: Uuid }): ReactElement {
  const t = useT();
  const aba = useAbaDoPiloto(clientId);
  const acoes = useAcoesDaAba(clientId);
  const [secao, setSecao] = useState<Secao>('resumo');
  const [rascunho, setRascunho] = useState<MemoryContent | null>(null);

  useEffect(() => {
    if (aba.data !== undefined) setRascunho(aba.data.memory);
  }, [aba.data?.memoryVersion]);

  if (aba.isPending) return <SkeletonRows rows={6} height={80} />;
  if (aba.isError || aba.data === undefined || rascunho === null) {
    return <EmptyState icon="robot" title={t.piloto.titulo} text={textoDoErro(aba.error)} />;
  }
  const d = aba.data;
  const a = t.piloto.aba;
  const sujo = JSON.stringify(rascunho) !== JSON.stringify(d.memory);
  const r: Rascunho = {
    m: rascunho,
    mudar: (parcial) => setRascunho((atual) => (atual === null ? atual : { ...atual, ...parcial })),
  };
  const contagem: Partial<Record<Secao, number>> = {
    aprendizados: d.learnings.length,
    testar: d.tests.filter((c) => c.lastResult !== null && !c.lastResult.ok).length,
  };

  return (
    <div className="piloto-aba">
      <nav className="piloto-nav" aria-label={t.piloto.titulo}>
        {SECOES.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`piloto-nav__item${secao === s.id ? ' is-on' : ''}`}
            aria-current={secao === s.id}
            onClick={() => setSecao(s.id)}
          >
            <Icon name={s.icone} size={15} />
            <span>{a.nav[s.id]}</span>
            {(contagem[s.id] ?? 0) > 0 ? (
              <span className="piloto-nav__conta num">{contagem[s.id]}</span>
            ) : null}
          </button>
        ))}
      </nav>

      <div className="card piloto-conteudo">
        <header className="piloto-conteudo__cabeca">
          <h3 className="piloto-conteudo__titulo">{a.nav[secao]}</h3>
          <p className="piloto-bloco__texto">{a.navTexto[secao]}</p>
        </header>

        {secao === 'resumo' ? <Resumo aba={d} clientId={clientId} ir={setSecao} /> : null}
        {secao === 'memoria' ? <Memoria aba={d} clientId={clientId} r={r} /> : null}
        {secao === 'estilo' ? <Estilo aba={d} clientId={clientId} r={r} /> : null}
        {secao === 'horarios' ? <Horarios aba={d} clientId={clientId} /> : null}
        {secao === 'testar' ? (
          <div className="piloto-testar">
            <Simulador clientId={clientId} />
            <Testes aba={d} clientId={clientId} />
          </div>
        ) : null}
        {secao === 'aprendizados' ? <Aprendizados aba={d} /> : null}
        {secao === 'historico' ? <Historico aba={d} clientId={clientId} /> : null}

        {sujo && (secao === 'memoria' || secao === 'estilo') ? (
          <BarraDeSalvar
            salvando={acoes.memoria.isPending}
            erro={acoes.memoria.isError ? textoDoErro(acoes.memoria.error) : null}
            onDescartar={() => setRascunho(d.memory)}
            onSalvar={() =>
              acoes.memoria.mutate({
                ...rascunho,
                estilo: {
                  ...rascunho.estilo,
                  frases: rascunho.estilo.frases.filter((f) => f.trim() !== ''),
                },
              })
            }
          />
        ) : null}
      </div>
    </div>
  );
}
